import {MessageDescriptor} from '@lingui/core';
import {t} from '@lingui/macro';

import {BasicActivityTypesEnum} from '../../models/api/activityModel';
import {RuleActionTypesEnum} from './ruleActionModel';
import {RuleConditionTypesEnum} from './ruleConditionModel';
import {RuleEventTypesEnum} from './ruleEventModel';

export enum RuleTriggersTypesEnum {
  ARCHIVE = 'archive',
  ASSIGN = 'assign',
  COMMENT = 'comment',
  INBOUND = 'inbound',
  MENTION = 'mention',
  MOVE = 'move',
  OUT_REPLY = 'out_reply',
  OUTBOUND = 'outbound',
  REOPEN = 'reopen',
  RESTORE = 'restore',
  SENDING_ERROR = 'sending_error',
  SNOOZE = 'snooze',
  SNOOZE_EXPIRED = 'snooze_expired',
  TAG = 'tag',
  TRASH = 'trash',
  UNASSIGN = 'unassign',
  UNTAG = 'untag',
  TOPIC_ADDED = 'topic_added',
  TOPIC_REMOVED = 'topic_removed',
}

export const triggersConditionsMap: {
  [key in RuleTriggersTypesEnum]?: ReadonlyArray<{type: RuleConditionTypesEnum; optional?: boolean}>;
} = {
  [RuleTriggersTypesEnum.COMMENT]: [{type: RuleConditionTypesEnum.COMMENT_BODY_AND_AUTHORS}],
  [RuleTriggersTypesEnum.MENTION]: [{type: RuleConditionTypesEnum.MENTIONS, optional: true}],
  [RuleTriggersTypesEnum.ASSIGN]: [
    {type: RuleConditionTypesEnum.ASSIGNEE_CHANGED},
    {type: RuleConditionTypesEnum.ASSIGNEE_IN, optional: true}, // Legacy one.
  ],
  [RuleTriggersTypesEnum.TAG]: [{type: RuleConditionTypesEnum.ADDED_TAG_IS, optional: true}],
  [RuleTriggersTypesEnum.UNTAG]: [{type: RuleConditionTypesEnum.REMOVED_TAG_IS, optional: true}],
};

export type RuleTriggerEventTypes = RuleEventTypesEnum | BasicActivityTypesEnum | RuleActionTypesEnum;

export interface RuleTriggerFromEventsDescription {
  title: MessageDescriptor;
  events: ReadonlyArray<RuleTriggerEventTypes>;
}

// @RULES TODO: Translate those strings.
export const RuleTriggerFromEventsDescription: Record<
  RuleTriggersTypesEnum,
  RuleTriggerFromEventsDescription
> = {
  [RuleTriggersTypesEnum.INBOUND]: {
    title: t('rule-event-inbound-title')`Inbound message is received`,
    events: [RuleEventTypesEnum.INBOUND],
  },
  [RuleTriggersTypesEnum.MOVE]: {
    title: t('rule-event-move-title')`Conversation is moved`,
    events: [BasicActivityTypesEnum.MOVE],
  },
  [RuleTriggersTypesEnum.OUTBOUND]: {
    title: t('rule-event-outbound-title')`Outbound message is sent (new conversation)`,
    events: [RuleEventTypesEnum.OUTBOUND],
  },
  [RuleTriggersTypesEnum.OUT_REPLY]: {
    title: t('rule-event-out-reply-title')`Outbound reply is sent (existing conversation)`,
    events: [RuleEventTypesEnum.OUT_REPLY],
  },
  [RuleTriggersTypesEnum.COMMENT]: {
    title: t('rule-event-comment-title')`New comment is added`,
    events: [RuleEventTypesEnum.COMMENT],
  },
  [RuleTriggersTypesEnum.MENTION]: {
    title: t('rule-event-mention-title')`Teammate is mentioned`,
    events: [RuleEventTypesEnum.MENTION],
  },
  [RuleTriggersTypesEnum.SENDING_ERROR]: {
    title: t('rule-event-sending-error-title')`Message delivery fails`,
    events: [BasicActivityTypesEnum.SENDING_ERROR, BasicActivityTypesEnum.MESSAGE_BOUNCE_ERROR],
  },
  [RuleTriggersTypesEnum.ARCHIVE]: {
    title: t('rule-event-archive-title')`Conversation is archived`,
    events: [BasicActivityTypesEnum.ARCHIVE],
  },
  [RuleTriggersTypesEnum.REOPEN]: {
    title: t('rule-event-reopen-title')`Conversation is reopened`,
    events: [RuleEventTypesEnum.REOPEN],
  },
  [RuleTriggersTypesEnum.TRASH]: {
    title: t('rule-event-trash-title')`Conversation is deleted`,
    events: [BasicActivityTypesEnum.TRASH],
  },
  [RuleTriggersTypesEnum.RESTORE]: {
    title: t('rule-event-restore-title')`Conversation is restored`,
    events: [BasicActivityTypesEnum.RESTORE],
  },
  [RuleTriggersTypesEnum.SNOOZE_EXPIRED]: {
    title: t('rule-event-snooze-expired-title')`Snooze expired`,
    events: [BasicActivityTypesEnum.REMINDER, BasicActivityTypesEnum.TRACKER_REMINDER],
  },
  [RuleTriggersTypesEnum.TAG]: {
    title: t('rule-event-tag-title')`Tag is added`,
    events: [RuleEventTypesEnum.TAG, RuleActionTypesEnum.TAG_PRIVATE],
  },
  [RuleTriggersTypesEnum.UNTAG]: {
    title: t('rule-event-untag-title')`Tag is removed`,
    events: [BasicActivityTypesEnum.UNTAG, RuleActionTypesEnum.UNTAG_PRIVATE],
  },
  [RuleTriggersTypesEnum.ASSIGN]: {
    title: t('rule-event-assign-title')`Assignee is changed`,
    events: [BasicActivityTypesEnum.ASSIGN],
  },
  [RuleTriggersTypesEnum.UNASSIGN]: {
    title: t('rule-event-unassign-title')`Assignee is removed`,
    events: [BasicActivityTypesEnum.UNASSIGN],
  },
  [RuleTriggersTypesEnum.TOPIC_ADDED]: {
    title: t('rule-event-topic-added-title')`Link is added`,
    events: [BasicActivityTypesEnum.TOPIC_ADDED],
  },
  [RuleTriggersTypesEnum.TOPIC_REMOVED]: {
    title: t('rule-event-topic-removed-title')`Link is removed`,
    events: [BasicActivityTypesEnum.TOPIC_REMOVED],
  },
  [RuleTriggersTypesEnum.SNOOZE]: {
    title: t('rule-event-snooze-title')`Conversation is snoozed`,
    events: [RuleEventTypesEnum.SNOOZE],
  },
};

export enum RuleEditorTriggerGroupsEnum {
  MESSAGE = 'message',
  STATUS = 'status',
  TEAMMATE = 'teammate',
  TAG = 'tag',
  TOPIC = 'topic',
}

// All the triggers available to the rule editor.
export const ruleTriggersGroups = [
  {
    id: RuleEditorTriggerGroupsEnum.MESSAGE,
    items: [
      RuleTriggersTypesEnum.INBOUND,
      RuleTriggersTypesEnum.MOVE,
      RuleTriggersTypesEnum.OUTBOUND,
      RuleTriggersTypesEnum.OUT_REPLY,
      RuleTriggersTypesEnum.SENDING_ERROR,
    ],
  },
  {
    id: RuleEditorTriggerGroupsEnum.STATUS,
    items: [
      RuleTriggersTypesEnum.ARCHIVE,
      RuleTriggersTypesEnum.REOPEN,
      RuleTriggersTypesEnum.TRASH,
      RuleTriggersTypesEnum.RESTORE,
      RuleTriggersTypesEnum.SNOOZE,
      RuleTriggersTypesEnum.SNOOZE_EXPIRED,
    ],
  },
  {
    id: RuleEditorTriggerGroupsEnum.TEAMMATE,
    items: [
      RuleTriggersTypesEnum.COMMENT,
      RuleTriggersTypesEnum.MENTION,
      RuleTriggersTypesEnum.ASSIGN,
      RuleTriggersTypesEnum.UNASSIGN,
    ],
  },
  {
    id: RuleEditorTriggerGroupsEnum.TAG,
    items: [RuleTriggersTypesEnum.TAG, RuleTriggersTypesEnum.UNTAG],
  },
  {
    id: RuleEditorTriggerGroupsEnum.TOPIC,
    items: [RuleTriggersTypesEnum.TOPIC_ADDED, RuleTriggersTypesEnum.TOPIC_REMOVED],
  },
];
